"use client"

import * as React from "react"
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Kbd } from "@/components/ui/kbd"
import { AppIcon } from "@/components/desk/AppIcon"
import { WidgetIcon } from "@/components/widgets/WidgetIcon"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { OpenAppTab, TabGroup } from "@/types/workspace"
import { Layout, Monitor, RotateCw, Pin, Plus, Split, PlusSquare, Trash2, X, Settings, Puzzle, User, ShoppingBag, Notebook, Calculator, CloudSun, Bot, Calendar, ChevronDown, Check } from "lucide-react"
import { toast } from "sonner"

interface DesktopChromeTabBarProps {
  openApps: OpenAppTab[]
  activeAppId: string | null
  setActiveAppId: (id: string | null) => void
  splitActiveAppId: string | null
  setSplitActiveAppId: (id: string | null) => void
  focusedPane: "left" | "right"
  setFocusedPane: (pane: "left" | "right") => void
  tabGroups: TabGroup[]
  setTabGroups: React.Dispatch<React.SetStateAction<TabGroup[]>>
  installedApps: any[]
  applicableWidgets?: any[]
  activeFloatingWidgets?: string[]
  handleToggleFloatingWidget?: (id: string) => void
  handleOpenApp: (app: any) => void
  handleAddNewTab?: () => void
  handleCloseApp: (instanceId: string) => void
  handleTogglePinApp: (instanceId: string) => void
  handleDuplicateApp: (tab: OpenAppTab) => void
  handleCreateTabGroup: (instanceId: string) => void
  handleToggleCollapseGroup?: (groupId: string) => void
  handleAddTabToGroup: (instanceId: string, groupId: string) => void
  handleRemoveTabFromGroup: (instanceId: string) => void
  handleRenameGroup: (groupId: string, newName?: string) => void
  handleChangeGroupColor: (groupId: string, color: string) => void
  handleUngroupAll: (groupId: string) => void
  handleCloseGroupTabs: (groupId: string) => void
  handleTabDragStart: (e: React.DragEvent, instanceId: string) => void
  handleTabDragOver: (e: React.DragEvent, instanceId?: string) => void
  handleTabDrop: (e: React.DragEvent, targetInstanceId?: string) => void
  handleGroupDragStart?: (e: React.DragEvent, groupId: string) => void
  handleGroupDragOver?: (e: React.DragEvent, targetInstanceId?: string, targetGroupId?: string) => void
  draggedTabId: string | null
  draggedGroupId?: string | null
  editingGroupId?: string | null
  setEditingGroupId?: React.Dispatch<React.SetStateAction<string | null>>
  isMac: boolean
  COLOR_MAP: Record<string, { border: string, bg: string, text: string, rawHex: string }>
  setIframeRefreshKeys: React.Dispatch<React.SetStateAction<Record<string, number>>>
  currentAccount?: any
  currentWorkspace?: any
  setCurrentWorkspace?: (ws: any) => void
  handleOpenSettingsTab?: () => void
  setSettingsTab?: (tab: any) => void
}

export function DesktopChromeTabBar({
  openApps,
  activeAppId,
  setActiveAppId,
  splitActiveAppId,
  setSplitActiveAppId,
  focusedPane,
  setFocusedPane,
  tabGroups,
  installedApps,
  applicableWidgets,
  activeFloatingWidgets,
  handleToggleFloatingWidget,
  handleOpenApp,
  handleAddNewTab,
  handleCloseApp,
  handleTogglePinApp,
  handleDuplicateApp,
  handleCreateTabGroup,
  handleToggleCollapseGroup,
  handleAddTabToGroup,
  handleRemoveTabFromGroup,
  handleRenameGroup,
  handleChangeGroupColor,
  handleUngroupAll,
  handleCloseGroupTabs,
  handleTabDragStart,
  handleTabDragOver,
  handleTabDrop,
  handleGroupDragStart,
  handleGroupDragOver,
  draggedTabId,
  draggedGroupId,
  editingGroupId,
  setEditingGroupId,
  isMac,
  COLOR_MAP,
  setIframeRefreshKeys,
  currentAccount,
  currentWorkspace,
  setCurrentWorkspace,
  handleOpenSettingsTab,
  setSettingsTab
}: DesktopChromeTabBarProps) {
  const [openGroupPopoverId, setOpenGroupPopoverId] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (editingGroupId) {
      setOpenGroupPopoverId(editingGroupId)
    }
  }, [editingGroupId])
  const [hoveredPreviewTab, setHoveredPreviewTab] = React.useState<{
    tab: OpenAppTab
    x: number
    y: number
  } | null>(null)
  const hoverTimerRef = React.useRef<NodeJS.Timeout | null>(null)

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  React.useEffect(() => {
    return () => clearHoverTimer()
  }, [])

  // Limpa o preview se a aba em exibição não existir mais na lista de abas abertas
  React.useEffect(() => {
    if (hoveredPreviewTab) {
      const tabStillExists = openApps.some(app => app.instanceId === hoveredPreviewTab.tab.instanceId)
      if (!tabStillExists) {
        clearHoverTimer()
        setHoveredPreviewTab(null)
      }
    }
  }, [openApps, hoveredPreviewTab])

  return (
    <div className="hidden md:flex h-11 w-full items-end justify-between bg-zinc-100 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-4 pt-1.5 select-none z-40 shrink-0 overflow-visible">
      {/* Lado Esquerdo: Abas e Botão Novo */}
      <div className="flex flex-1 items-end gap-1 overflow-x-auto overflow-y-hidden scrollbar-none">
        {/* Aba do Dashboard (Início) com Nome do Workspace ao lado em Azul Bold */}
        <ContextMenu>
          <ContextMenuTrigger
            render={
              <div
                onClick={() => { setActiveAppId(null); setSplitActiveAppId(null); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-t-lg transition-all cursor-pointer shrink-0 border-t border-x ${activeAppId === null
                  ? "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white border-zinc-300 dark:border-zinc-750 shadow-sm z-10 -mb-[1px]"
                  : "text-zinc-500 hover:text-zinc-855 dark:text-zinc-400 dark:hover:text-zinc-250 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/30 border-zinc-200/50 dark:border-zinc-800/40"
                  }`}
              >
                <Layout className="size-3.5 text-[#004EB1] shrink-0" />
                <span className="font-bold text-xs">Início</span>
                <span className="text-zinc-300 dark:text-zinc-700 font-mono text-xs select-none">/</span>

                {/* Seletor de Workspace com texto em Azul Bold */}
                <Popover>
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs font-semibold text-[#004EB1] dark:text-blue-400 hover:opacity-80 transition-opacity cursor-pointer select-none"
                    title="Alternar Workspace"
                  >
                    <span className="max-w-[130px] truncate">
                      {currentWorkspace?.name || currentAccount?.name || "Workspace"}
                    </span>
                    <ChevronDown className="size-3 text-[#004EB1] dark:text-blue-400 opacity-70 shrink-0" />
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={6}
                    className="w-64 p-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl font-sans text-xs"
                  >
                    <div className="px-2 py-1.5 mb-1 border-b border-zinc-100 dark:border-zinc-800">
                      <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                        Alternar Workspace
                      </p>
                      <p className="text-[11px] text-zinc-500 truncate">
                        {currentAccount?.name || "Sua Conta"}
                      </p>
                    </div>

                    <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                      {(currentAccount?.workspaces || []).filter((w: any) => w.status !== false).length === 0 ? (
                        <p className="text-[11px] text-zinc-400 dark:text-zinc-500 italic p-3 text-center">
                          Nenhum workspace disponível para seu usuário.
                        </p>
                      ) : (
                        (currentAccount?.workspaces || []).filter((w: any) => w.status !== false).map((ws: any) => {
                          const isActive = currentWorkspace?.id === ws.id
                          return (
                            <button
                              key={ws.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (setCurrentWorkspace) setCurrentWorkspace(ws)
                              }}
                              className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors cursor-pointer ${isActive
                                ? "bg-blue-500/10 text-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400 font-bold border border-blue-500/20"
                                : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60 text-zinc-700 dark:text-zinc-300"
                                }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                <div className={`size-6 rounded-md flex items-center justify-center font-bold text-[10px] shrink-0 ${isActive
                                  ? "bg-[#004EB1] text-white"
                                  : "bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                                  }`}>
                                  {ws.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="truncate">
                                  <p className="truncate text-xs leading-tight">{ws.name}</p>
                                  <p className="text-[10px] text-zinc-400 font-mono truncate">{ws.slug}</p>
                                </div>
                              </div>

                              {isActive && (
                                <Check className="size-4 text-[#004EB1] shrink-0" />
                              )}
                            </button>
                          )
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            }
          />
          <ContextMenuContent className="w-48 font-sans">
            <ContextMenuItem onClick={() => { setActiveAppId(null); setSplitActiveAppId(null); }} className="cursor-pointer text-xs py-1.5 flex items-center gap-1.5">
              <Layout className="size-3.5 text-zinc-500" />
              <span>Ir para o Início</span>
            </ContextMenuItem>
            <ContextMenuItem onClick={() => window.location.reload()} className="cursor-pointer text-xs py-1.5 flex items-center gap-1.5">
              <Monitor className="size-3.5 text-zinc-500" />
              <span>Recarregar Sistema</span>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        {/* Abas Dinâmicas de Apps Abertos */}
        {openApps.map((tab, index) => {
          const isActiveLeft = activeAppId === tab.instanceId
          const isActiveRight = splitActiveAppId === tab.instanceId
          const isActive = isActiveLeft || isActiveRight
          const isFocusedTab = splitActiveAppId !== null
            ? ((isActiveLeft && focusedPane === "left") || (isActiveRight && focusedPane === "right"))
            : isActiveLeft
          const group = tab.groupId ? tabGroups.find((g) => g.id === tab.groupId) : null
          const groupStyles = group ? COLOR_MAP[group.color] : null
          const isFirstInGroup = tab.groupId && openApps.findIndex((t) => t.groupId === tab.groupId) === index
          const isCollapsed = group?.collapsed ?? false

          const groupElement = isFirstInGroup && group ? (
            <Popover
              key={`group-popover-${group.id}`}
              open={openGroupPopoverId === group.id}
              onOpenChange={(isOpen) => {
                if (!isOpen) {
                  setOpenGroupPopoverId(null)
                  if (setEditingGroupId) setEditingGroupId(null)
                } else {
                  setOpenGroupPopoverId(group.id)
                }
              }}
            >
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => handleGroupDragStart && handleGroupDragStart(e, group.id)}
                    onDragOver={(e) => handleGroupDragOver && handleGroupDragOver(e, undefined, group.id)}
                    onDrop={handleTabDrop}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (handleToggleCollapseGroup) handleToggleCollapseGroup(group.id)
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setOpenGroupPopoverId(group.id)
                    }}
                    className={`h-7 px-2.5 mb-1.5 rounded-lg text-[10px] font-bold border transition-all cursor-pointer select-none flex items-center gap-1.5 shrink-0 ${groupStyles?.bg} ${groupStyles?.text} ${groupStyles?.border} hover:opacity-90 active:scale-95 ${draggedGroupId === group.id ? "opacity-50 scale-95 ring-2 ring-[#004EB1]/50" : ""
                      }`}
                    title="Clique para recolher/expandir. Clique com o botão direito para configurar."
                  >
                    <span className="size-2 rounded-full shrink-0" style={{ backgroundColor: COLOR_MAP[group.color]?.rawHex }} />
                    <span className="max-w-[120px] truncate">{group.name || "Novo Grupo"}</span>
                    {isCollapsed && (
                      <span className="text-[9px] px-1 py-0.5 rounded-full bg-black/10 dark:bg-white/10 font-bold ml-0.5 shrink-0">
                        {openApps.filter((t) => t.groupId === group.id).length}
                      </span>
                    )}
                  </button>
                }
              />
              <PopoverContent
                align="start"
                sideOffset={6}
                className="w-64 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-2xl font-sans z-[99999]"
              >
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider block mb-1">
                      Nome do Grupo
                    </label>
                    <input
                      type="text"
                      autoFocus
                      maxLength={30}
                      value={group.name}
                      onChange={(e) => handleRenameGroup(group.id, e.target.value.slice(0, 30))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setOpenGroupPopoverId(null)
                          if (setEditingGroupId) setEditingGroupId(null)
                        }
                      }}
                      placeholder="Nome do grupo..."
                      className="w-full text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-[#004EB1]"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider block mb-1.5">
                      Cor do Grupo
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.keys(COLOR_MAP).map((color) => {
                        const isSelected = group.color === color
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => handleChangeGroupColor(group.id, color)}
                            className={`size-5 rounded-full transition-transform cursor-pointer border ${isSelected ? "ring-2 ring-offset-1 ring-zinc-600 dark:ring-zinc-300 scale-110" : "hover:scale-105"
                              }`}
                            style={{ backgroundColor: COLOR_MAP[color].rawHex }}
                            title={color}
                          />
                        )
                      })}
                    </div>
                  </div>

                  <div className="border-t border-zinc-100 dark:border-zinc-800 pt-2 space-y-1">
                    <button
                      type="button"
                      onClick={() => {
                        handleUngroupAll(group.id)
                        setOpenGroupPopoverId(null)
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors cursor-pointer"
                    >
                      Desagrupar Tudo
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearHoverTimer()
                        setHoveredPreviewTab(null)
                        handleCloseGroupTabs(group.id)
                        setOpenGroupPopoverId(null)
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-md transition-colors cursor-pointer"
                    >
                      Fechar Todas as Guias
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : null

          // Se o grupo estiver colapsado, só renderiza o badge na primeira aba e oculta as demais
          if (group && isCollapsed) {
            return groupElement
          }

          return (
            <React.Fragment key={tab.instanceId}>
              {/* Badge de Grupo de Abas */}
              {groupElement}

              {/* Elemento da Aba */}
              <ContextMenu>
                <ContextMenuTrigger
                  render={
                    <div
                      draggable
                      onDragStart={(e) => handleTabDragStart(e, tab.instanceId)}
                      onDragOver={(e) => handleTabDragOver(e, tab.instanceId)}
                      onDrop={(e) => handleTabDrop(e, tab.instanceId)}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        clearHoverTimer()
                        hoverTimerRef.current = setTimeout(() => {
                          setHoveredPreviewTab({
                            tab,
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + 8
                          })
                        }, 1000)
                      }}
                      onMouseLeave={() => {
                        clearHoverTimer()
                        setHoveredPreviewTab(null)
                      }}
                      onClick={() => {
                        clearHoverTimer()
                        setHoveredPreviewTab(null)
                        if (splitActiveAppId !== null) {
                          if (focusedPane === "left") {
                            if (tab.instanceId === splitActiveAppId) {
                              setSplitActiveAppId(activeAppId)
                            }
                            setActiveAppId(tab.instanceId)
                          } else {
                            if (tab.instanceId === activeAppId) {
                              setActiveAppId(splitActiveAppId)
                            }
                            setSplitActiveAppId(tab.instanceId)
                          }
                        } else {
                          setActiveAppId(tab.instanceId)
                        }
                      }}
                      className={`group flex items-center gap-1.5 rounded-t-lg transition-all duration-150 cursor-pointer relative select-none shrink-0 border-t border-x ${tab.pinned ? "px-3 py-2 w-11 justify-center shrink-0" : "px-3 py-2 max-w-[170px] min-w-[50px] sm:min-w-[100px] flex-1 shrink"
                        } ${isActive
                          ? isFocusedTab
                            ? "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white border-zinc-300 dark:border-zinc-750 shadow-sm z-10 -mb-[1px]"
                            : "bg-zinc-50/90 dark:bg-zinc-900/80 text-zinc-650 dark:text-zinc-300 border-zinc-255 dark:border-zinc-800 shadow-sm z-10 -mb-[1px]"
                          : "text-zinc-500 hover:text-zinc-855 dark:text-zinc-400 dark:hover:text-zinc-250 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/30 border-zinc-200/50 dark:border-zinc-800/40"
                        } ${draggedTabId === tab.instanceId ? "opacity-50 ring-2 ring-[#004EB1]/50 bg-[#004EB1]/10 dark:bg-blue-950/40 z-30 scale-95" : ""
                        }`}
                      style={
                        group
                          ? {
                            borderTopColor: COLOR_MAP[group.color]?.rawHex,
                            borderTopWidth: '3px',
                            borderTopStyle: 'solid'
                          }
                          : undefined
                      }
                    >
                      {tab.app.id === "settings" || tab.app.id === "system-settings" ? (
                        <Settings className="size-3.5 text-[#004EB1] shrink-0" />
                      ) : tab.app.id === "profile" || tab.app.id === "system-profile" ? (
                        <User className="size-3.5 text-[#004EB1] shrink-0" />
                      ) : tab.app.id === "marketplace" ? (
                        <ShoppingBag className="size-3.5 text-[#004EB1] shrink-0" />
                      ) : tab.app.id === "system-notes" || tab.app.name.toLowerCase().includes("nota") ? (
                        <Notebook className="size-3.5 text-amber-500 shrink-0" />
                      ) : tab.app.id === "system-calculator" || tab.app.name.toLowerCase().includes("calculadora") ? (
                        <Calculator className="size-3.5 text-purple-500 shrink-0" />
                      ) : tab.app.id === "system-weather" || tab.app.name.toLowerCase().includes("clima") ? (
                        <CloudSun className="size-3.5 text-[#004EB1] shrink-0" />
                      ) : tab.app.id === "bold-agents" ? (
                        <Bot className="size-3.5 text-blue-500 shrink-0" />
                      ) : tab.app.slug === "new-tab" || tab.app.id.startsWith("new-tab") ? (
                        <Plus className="size-3.5 text-[#004EB1] dark:text-blue-400 shrink-0" />
                      ) : (
                        <AppIcon iconUrl={tab.app.iconUrl} iframeUrl={tab.app.iframeUrl} name={tab.app.name} size="small" />
                      )}

                      {!tab.pinned && (
                        <>
                          <div className="flex items-center gap-1 min-w-0 pr-5 overflow-hidden">
                            <span className="font-semibold text-xs whitespace-nowrap truncate">{tab.app.name}</span>
                            {splitActiveAppId !== null && isActiveLeft && (
                              <span className="px-1 py-0.5 text-[8px] font-bold bg-[#004EB1]/10 text-[#004EB1] dark:bg-blue-550/20 dark:text-blue-400 rounded-sm shrink-0 scale-90">P1</span>
                            )}
                            {splitActiveAppId !== null && isActiveRight && (
                              <span className="px-1 py-0.5 text-[8px] font-bold bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400 rounded-sm shrink-0 scale-90">P2</span>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearHoverTimer();
                              setHoveredPreviewTab(null);
                              handleCloseApp(tab.instanceId);
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 size-4 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-200/80 dark:hover:bg-zinc-800 transition-colors z-20 cursor-pointer"
                          >
                            &times;
                          </button>
                        </>
                      )}
                    </div>
                  }
                />

                <ContextMenuContent className="w-56 font-sans">
                  <ContextMenuItem onClick={() => {
                    const iframe = document.querySelector(`iframe[data-instance-id="${tab.instanceId}"]`) as HTMLIFrameElement
                    if (iframe) {
                      try {
                        iframe.src = iframe.src
                      } catch (e) {
                        setIframeRefreshKeys(prev => ({
                          ...prev,
                          [tab.instanceId]: (prev[tab.instanceId] || 0) + 1
                        }))
                      }
                    } else {
                      setIframeRefreshKeys(prev => ({
                        ...prev,
                        [tab.instanceId]: (prev[tab.instanceId] || 0) + 1
                      }))
                    }
                  }} className="cursor-pointer text-xs py-1.5 flex items-center gap-1.5">
                    <RotateCw className="size-3.5 text-zinc-500" />
                    <span>Recarregar Guia</span>
                  </ContextMenuItem>

                  <ContextMenuSeparator />

                  <ContextMenuItem onClick={() => handleTogglePinApp(tab.instanceId)} className="cursor-pointer text-xs py-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Pin className="size-3.5 text-zinc-500" />
                      <span>{tab.pinned ? "Desafixar Guia" : "Fixar Guia"}</span>
                    </div>
                    <Kbd>{isMac ? "Cmd + P" : "Alt + P"}</Kbd>
                  </ContextMenuItem>

                  <ContextMenuItem onClick={() => handleDuplicateApp(tab)} className="cursor-pointer text-xs py-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Plus className="size-3.5 text-zinc-500" />
                      <span>Duplicar Guia</span>
                    </div>
                    <Kbd>{isMac ? "Cmd + D" : "Alt + D"}</Kbd>
                  </ContextMenuItem>

                  <ContextMenuItem
                    onClick={() => {
                      if (splitActiveAppId === null) {
                        if (tab.instanceId === activeAppId) {
                          setSplitActiveAppId("empty")
                          setFocusedPane("right")
                        } else {
                          setSplitActiveAppId(tab.instanceId)
                          setFocusedPane("right")
                        }
                      } else {
                        setSplitActiveAppId(null)
                      }
                    }}
                    className="cursor-pointer text-xs py-1.5 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-1.5">
                      <Split className="size-3.5 text-zinc-500" />
                      <span>{splitActiveAppId === null ? "Dividir Tela" : "Fechar Divisão"}</span>
                    </div>
                    <Kbd>{isMac ? "Cmd + S" : "Alt + S"}</Kbd>
                  </ContextMenuItem>

                  <ContextMenuSeparator />

                  {tabGroups.map((g) => (
                    <ContextMenuItem
                      key={g.id}
                      onClick={() => handleAddTabToGroup(tab.instanceId, g.id)}
                      className="cursor-pointer text-xs py-1.5 flex items-center gap-2"
                    >
                      <span className="size-2 rounded-full" style={{ backgroundColor: COLOR_MAP[g.color]?.rawHex }} />
                      <span>Grupo: {g.name}</span>
                    </ContextMenuItem>
                  ))}

                  {tab.groupId && (
                    <ContextMenuItem onClick={() => handleRemoveTabFromGroup(tab.instanceId)} className="cursor-pointer text-xs py-1.5 flex items-center gap-1.5">
                      <Trash2 className="size-3.5 text-red-500" />
                      <span>Remover do Grupo</span>
                    </ContextMenuItem>
                  )}

                  <ContextMenuSeparator />

                  <ContextMenuItem onClick={() => {
                    clearHoverTimer()
                    setHoveredPreviewTab(null)
                    handleCloseApp(tab.instanceId)
                  }} variant="destructive" className="cursor-pointer text-xs py-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <X className="size-3.5" />
                      <span>Fechar Guia</span>
                    </div>
                    <Kbd>{isMac ? "Cmd + W" : "Alt + W"}</Kbd>
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </React.Fragment>
          )
        })}

        {/* Botão "+" para abrir Nova Guia */}
        <button
          type="button"
          onClick={() => {
            clearHoverTimer()
            setHoveredPreviewTab(null)
            if (handleAddNewTab) handleAddNewTab()
          }}
          title="Nova Guia"
          className="size-7 flex items-center justify-center rounded-md hover:bg-zinc-250 dark:hover:bg-zinc-800 text-zinc-500 hover:text-zinc-855 dark:text-zinc-450 dark:hover:text-zinc-250 transition-colors mb-1.5 shrink-0 ml-1 cursor-pointer"
        >
          <Plus className="size-4" />
        </button>
      </div>



      {/* Chrome Style Tab Hover Preview Card */}
      {hoveredPreviewTab && (
        <div
          className="fixed z-[9999] pointer-events-none animate-in fade-in zoom-in-95 duration-200"
          style={{
            left: `${Math.max(16, Math.min(window.innerWidth - 260, hoveredPreviewTab.x - 120))}px`,
            top: `${hoveredPreviewTab.y}px`
          }}
        >
          <div className="w-[240px] bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-zinc-200/90 dark:border-zinc-800/90 rounded-2xl p-3 shadow-2xl space-y-2.5 font-sans">
            <div className="flex items-center gap-2 min-w-0">
              {hoveredPreviewTab.tab.app.id === "settings" ? (
                <Settings className="size-4 text-[#004EB1] shrink-0" />
              ) : hoveredPreviewTab.tab.app.id === "profile" ? (
                <User className="size-4 text-[#004EB1] shrink-0" />
              ) : hoveredPreviewTab.tab.app.id === "marketplace" ? (
                <ShoppingBag className="size-4 text-[#004EB1] shrink-0" />
              ) : hoveredPreviewTab.tab.app.slug === "new-tab" || hoveredPreviewTab.tab.app.id.startsWith("new-tab") ? (
                <Plus className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
              ) : (
                <div className="shrink-0">
                  <AppIcon iconUrl={hoveredPreviewTab.tab.app.iconUrl} iframeUrl={hoveredPreviewTab.tab.app.iframeUrl} name={hoveredPreviewTab.tab.app.name} size="small" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate leading-tight">
                  {hoveredPreviewTab.tab.app.name}
                </h4>
                <p className="text-[10px] text-zinc-450 dark:text-zinc-500 truncate leading-none mt-0.5">
                  {hoveredPreviewTab.tab.app.slug === "new-tab" || hoveredPreviewTab.tab.app.id.startsWith("new-tab")
                    ? "Página Inicial da Guia"
                    : hoveredPreviewTab.tab.app.slug ? `alpha.boldworkspace.com.br/${hoveredPreviewTab.tab.app.slug}` : "Aplicativo Bold"}
                </p>
              </div>
            </div>

            <div className="w-full h-28 rounded-xl bg-zinc-100 dark:bg-zinc-950 border border-zinc-200/60 dark:border-zinc-800/60 overflow-hidden relative flex flex-col items-center justify-center p-3 text-center">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-transparent pointer-events-none" />
              <div className="size-9 rounded-xl bg-white dark:bg-zinc-900 shadow-md border border-zinc-200/40 dark:border-zinc-800/40 flex items-center justify-center mb-1.5 shrink-0">
                <AppIcon iconUrl={hoveredPreviewTab.tab.app.iconUrl} iframeUrl={hoveredPreviewTab.tab.app.iframeUrl} name={hoveredPreviewTab.tab.app.name} size="medium" />
              </div>
              <span className="text-[10px] font-bold text-zinc-700 dark:text-zinc-300 truncate max-w-full px-2">
                {hoveredPreviewTab.tab.app.name}
              </span>
              <span className={`text-[8px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 ${hoveredPreviewTab.tab.instanceId === activeAppId
                ? "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400"
                : "bg-zinc-200/60 text-zinc-500 dark:bg-zinc-800/60 dark:text-zinc-400"
                }`}>
                {hoveredPreviewTab.tab.instanceId === activeAppId ? "● Guia Ativa" : "Guia em 2º Plano"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
